import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  TextField, 
  InputAdornment, 
  Box, 
  CircularProgress,
  Divider,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import { TopBar } from '../../components/top_bar/TopBar';
import { Helmet } from 'react-helmet';
import SearchIcon from '@mui/icons-material/Search';
import { topMockInterviews } from '../../components/top_job/top_job_const';
import InterviewCard from '../../components/top_job/InterviewCard';
import './TopInterview.css';

const TopInterview = () => {
  const [interviews, setInterviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const TITLE = 'Top Interviews - Mirwork';
  const theme = useTheme();

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setInterviews(topMockInterviews);
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const filteredInterviews = interviews.filter(interview =>
    interview.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interview.jobName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
    <Helmet>
        <title>{TITLE}</title>
    </Helmet>
    <TopBar />
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', pb: 8 }}>
      <Box 
        component="header" 
        sx={{ 
          py: { xs: 5, md: 8 }, 
          mb: 6,
          position: "relative",
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: "white",
          borderRadius: { xs: 0, md: "0 0 30px 30px" },
          boxShadow: 3
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Mock Interview Practice
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4,
              maxWidth: '800px', 
              mx: 'auto',
              opacity: 0.9,
              fontSize: { xs: '1.1rem', md: '1.3rem' }
            }}
          >
            Practice with realistic mock interviews from top companies. Improve your skills and land your dream job.
          </Typography>
          
          <Paper
            elevation={6}
            sx={{
              p: 0.5,
              maxWidth: '600px',
              mx: 'auto',
              borderRadius: 2
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by company or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
            />
          </Paper>
        </Container>
      </Box>
      
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom
          sx={{ 
            mb: 4, 
            fontWeight: 600,
            fontSize: { xs: '1.75rem', md: '2.25rem' }
          }}
        >
          {searchTerm ? 'Search Results' : 'Top Mock Interviews'}
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : filteredInterviews.length > 0 ? (
          <Grid container spacing={3}>
            {filteredInterviews.map((interview) => (
              <Grid item xs={12} sm={6} md={4} key={interview.id}>
                <InterviewCard interview={interview} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', my: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No interviews found matching your search.
            </Typography>
          </Box>
        )}
        
        {!searchTerm && (
          <>
            <Divider sx={{ my: 8 }} />
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ 
                  mb: 4, 
                  fontWeight: 600,
                  fontSize: { xs: '1.75rem', md: '2.25rem' }
                }}
              >
                Why Practice With Mock Interviews?
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Realistic Experience
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Our mock interviews simulate real interview scenarios from top companies, helping you prepare for the real thing.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Detailed Feedback
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Get actionable feedback on your responses to help you improve your interviewing skills.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Build Confidence
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Practice makes perfect. Build your confidence through repeated practice in a low-stakes environment.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Container>
    </Box>
    </>
  );
};

export default TopInterview;